import { Billboard, Html } from '@react-three/drei';
import { type ThreeEvent } from '@react-three/fiber';
import { Suspense, useCallback, useMemo } from 'react';
import * as THREE from 'three';
import type { PlaneMeshProps } from '../types';
import { RENDER_ORDER_MARKER } from './RenderOrder';
import { ViewerLayer } from './ViewerLayer';
import { useOnClick } from './useOnClick';
import { PlaneMesh } from './PlaneMesh';
import { Billboard as FixedBillboard } from './Billboard';

type Props = {
  position: Parameters<THREE.Vector3['set']>;
  rotation?: Parameters<THREE.Euler['set']>;
  divStyle?: React.CSSProperties;
  onClick?: () => void;
  onHover?: (isHovered: boolean) => void;
  children?: React.ReactNode;
  scale?: number;
  markerHeight?: number;
  fixedOnScreenSize?: boolean;
  width?: number;
  height?: number;
};

const zIndexRange = [100, 0];
const noPointerStyle: React.CSSProperties = {
  pointerEvents: 'none',
  padding: '16px 32px',
  borderRadius: '4px',
};

const planeMeshProps: PlaneMeshProps = {
  name: '',
  position: [0, 0, 0],
  rotation: [0, 0, 0],
  scale: [1, 1, 1],
  color: 0xffffff,
  transparent: true,
  opacity: 0,
};

const defaultMarkerHeight = 1;
const defaultScale = 1;

const scaleAdjustmentFactor = 0.5;

export const HtmlMarker = ({
  divStyle,
  position,
  onClick,
  onHover,
  children,
  scale = defaultScale,
  markerHeight = defaultMarkerHeight,
  fixedOnScreenSize = false,
  width = 200,
  height = 100,
}: Props) => {
  const handleOnHover = useCallback(
    (isHovered0: boolean) => {
      onHover?.(isHovered0);
    },
    [onHover]
  );

  const onMarkerClick = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      event.stopPropagation();
      if (onClick) {
        onClick();
      }
    },
    [onClick]
  );

  const { onPointerDown, onPointerUp } = useOnClick({ onClick: onMarkerClick });

  const referenceCallback = useCallback((rootNode: THREE.Object3D | null) => {
    function enableLayers(node: THREE.Object3D | null) {
      if (node != null) {
        node.layers.enable(ViewerLayer.AutoPickable);
        node.layers.enable(ViewerLayer.Default);
        node.layers.enable(ViewerLayer.SphericalViewerLayer);
        node.renderOrder = RENDER_ORDER_MARKER;
        node.children.forEach((childNode) => enableLayers(childNode));
      }
    }

    enableLayers(rootNode);
  }, []);

  const handleOnPointerOver = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      event.stopPropagation();
      handleOnHover(true);
    },
    [handleOnHover]
  );

  const handleOnPointerOut = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      event.stopPropagation();
      handleOnHover(false);
    },
    [handleOnHover]
  );

  // Unfortunately drei/html doesn't trigger pointer events, so using invisible plane mesh to capture click and hover events.
  // Known issue that plane mesh size can be slightly different than visible marker size (depends on html content).

  const needPlane = !!(onClick || onHover);

  const planeComp = useMemo(
    () => (
      <>
        {needPlane && (
          <FixedBillboard position={position} scale={[width, height, 1]} respectPixelRatio={true}>
            <PlaneMesh {...planeMeshProps} onHover={handleOnHover} onClick={onClick} />
          </FixedBillboard>
        )}
      </>
    ),
    [onClick, handleOnHover, needPlane, position, width, height]
  );

  if (fixedOnScreenSize) {
    return (
      <>
        <Html
          as="div"
          center
          zIndexRange={zIndexRange}
          style={divStyle ?? noPointerStyle}
          position={position}
        >
          {children}
        </Html>
        {planeComp}
      </>
    );
  }

  return (
    <>
      <Suspense>
        <object3D
          ref={referenceCallback}
          position={position}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onPointerOver={handleOnPointerOver}
          onPointerOut={handleOnPointerOut}
        >
          <Billboard position={[0, 0, scale * markerHeight]} scale={scale * scaleAdjustmentFactor}>
            <Html transform as="div" zIndexRange={zIndexRange} style={divStyle ?? noPointerStyle}>
              {children}
            </Html>
          </Billboard>
        </object3D>
      </Suspense>
    </>
  );
};
